import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CalendarClock, CheckCircle2, RefreshCw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { FindoSpinner } from "@/components/FindoSpinner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso, lang) => (iso ? new Date(iso).toLocaleDateString(lang || "fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "");

export default function RenewContract() {
  const { t, lang } = useLang();
  const { token } = useParams();
  const [info, setInfo] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/quotes/${token}/renew-info`);
        if (!r.ok) throw new Error(t("pub.contractNotFound"));
        setInfo(await r.json());
      } catch (e) { setInfo(null); setError(e.message); }
    })();
  }, [token]);

  async function confirm() {
    setBusy(true);
    try {
      const r = await fetch(`${API}/quotes/${token}/renew`, { method: "POST" });
      if (!r.ok) throw new Error(t("pub.renewalFailed"));
      setDone(await r.json());
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-findo-cream flex items-center justify-center p-6" data-testid="renew-page">
      <div className="w-full max-w-md bg-white rounded-3xl border border-border card-shadow p-8">
        <div className="h-14 w-14 rounded-2xl bg-findo-navy/10 flex items-center justify-center mb-5">
          <CalendarClock size={26} className="text-findo-navy" />
        </div>

        {info === undefined && !error && (
          <p className="text-slate-500" data-testid="renew-loading">{t("pub.loadingContract")}</p>
        )}

        {error && !done && (
          <div data-testid="renew-error">
            <div className="flex items-center gap-2 text-findo-danger mb-2"><AlertTriangle size={20} /><h1 className="font-display text-xl font-bold">{t("pub.oops")}</h1></div>
            <p className="text-slate-600 text-sm">{error}</p>
            <Link to="/" className="inline-block mt-5 text-sm font-semibold text-findo-navy underline">{t("pub.backHome")}</Link>
          </div>
        )}

        {done && (
          <div data-testid="renew-success">
            <div className="flex items-center gap-2 text-emerald-600 mb-2"><CheckCircle2 size={22} /><h1 className="font-display text-2xl font-bold text-findo-navy">{t("pub.contractRenewed")}</h1></div>
            <p className="text-slate-600 text-sm mt-1">{t("pub.contractRenewedFor").replace("{n}", done.contract_months)}</p>
            <p className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800">
              {t("pub.newDeadline")} : <b data-testid="renew-new-expiry">{fmt(done.contract_expiry, lang)}</b>
            </p>
            <Link to="/" className="inline-block mt-6 text-sm font-semibold text-findo-navy underline">{t("pub.backHome")}</Link>
          </div>
        )}

        {info && !done && !error && (
          <div data-testid="renew-form">
            <h1 className="font-display text-2xl font-bold text-findo-navy">{t("pub.renewYourContract")}</h1>
            <p className="text-slate-500 text-sm mt-1">{info.organization}</p>
            <div className="mt-5 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">{t("pub.contractLabel")}</span><b className="font-mono text-findo-navy">{info.number}</b></div>
              <div className="flex justify-between"><span className="text-slate-500">{t("pub.currentDeadline")}</span><b>{fmt(info.contract_expiry, lang)}</b></div>
              <div className="flex justify-between"><span className="text-slate-500">{t("pub.duration")}</span><b>{info.contract_months} {t("pub.monthsWord")}</b></div>
              <div className="flex justify-between text-emerald-700"><span>{t("pub.newDeadline")}</span><b data-testid="renew-preview">{fmt(info.new_expiry_preview, lang)}</b></div>
            </div>
            <Button onClick={confirm} disabled={busy} className="w-full mt-6 rounded-full bg-findo-navy font-semibold h-11" data-testid="renew-confirm-btn">
              <RefreshCw size={16} className="mr-2" /> {busy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("pub.renewing")}</span> : t("pub.confirmRenewal")}
            </Button>
            <p className="text-xs text-slate-400 text-center mt-3">{t("pub.immediateRenewal")}</p>
          </div>
        )}
      </div>
    </div>
  );
}
